// src/tabs/logements/LogementGalleryEditor.jsx — Galerie centrale du logement
//
// Éditeur galerie photos AU NIVEAU LOGEMENT (Option B step 1). Renommé depuis
// LivretGalleryEditor : la galerie a été promue galerie centrale du logement.
// Le Livret + le Website restent CONSOMMATEURS (rendu) ; l'ÉDITION est ici,
// dans l'onglet « Info FBZ » du logement (section « Photos du logement »).
//
// Table : logement_gallery (ex-livret_gallery). Bucket Storage inchangé
// ('livret-photos' — pas de déplacement d'objets).
//
// Auto-chargement : ce composant fetch sa propre galerie via livret_admin_data
// (qui retourne { gallery }) car InfoFbzTab ne la charge pas.
//
// Pattern signed URL upload (inchangé) :
//   1. POST livret_photo_upload_signed_url { logement_id, filename, scope: 'gallery' }
//      → { signed_url, public_url, storage_path }
//   2. PUT signed_url avec File body (direct Storage)
//   3. POST logement_gallery_upsert { logement_id, storage_path, public_url, alt_text }
//
// F-MOY-2 : si POST logement_gallery_upsert échoue après upload OK → DELETE Storage path.

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";

// Helper auth headers admin
async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Compression image optionnelle (F-MOY-Safari : try/catch fallback file original)
async function maybeCompress(file) {
  try {
    const { default: imageCompression } = await import(
      "browser-image-compression"
    );
    return await imageCompression(file, {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
    });
  } catch (_) {
    // Safari iOS 13 ou tout autre échec → file original (limite 5MB serveur)
    return file;
  }
}

export default function LogementGalleryEditor({ logement_id }) {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-chargement galerie (InfoFbzTab ne charge pas la galerie lui-même).
  const reload = useCallback(async () => {
    if (!logement_id) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(logement_id)}`,
        { method: "GET", headers },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      }
      setGallery(Array.isArray(json.gallery) ? json.gallery : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [logement_id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleUpload = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = ""; // reset input
      if (!file) return;
      setUploading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const compressed = await maybeCompress(file);

        // 1. Signed URL gallery scope (R-2 sub-folder)
        const sigResp = await fetch(
          "/api/beds24-mapping?action=livret_photo_upload_signed_url",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "livret_photo_upload_signed_url",
              logement_id,
              filename: file.name,
              scope: "gallery",
            }),
          },
        );
        const sigJson = await sigResp.json();
        if (!sigResp.ok) {
          throw new Error(sigJson.message || sigJson.error || "signed_url_failed");
        }

        // 2. PUT direct Storage (signed URL)
        const putResp = await fetch(sigJson.signed_url, {
          method: "PUT",
          headers: { "Content-Type": compressed.type || "image/jpeg" },
          body: compressed,
        });
        if (!putResp.ok) {
          throw new Error(`Upload Storage échoué (HTTP ${putResp.status})`);
        }

        // 3. INSERT row logement_gallery
        const insResp = await fetch(
          "/api/beds24-mapping?action=logement_gallery_upsert",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "logement_gallery_upsert",
              logement_id,
              storage_path: sigJson.storage_path,
              public_url: sigJson.public_url,
              alt_text: {},
            }),
          },
        );
        const insJson = await insResp.json();
        if (!insResp.ok) {
          // F-MOY-2 like : rollback Storage (best-effort)
          try {
            await fetch("/api/beds24-mapping?action=livret_photo_delete", {
              method: "POST",
              headers,
              body: JSON.stringify({
                action: "livret_photo_delete",
                storage_path: sigJson.storage_path,
              }),
            });
          } catch (_) {
            /* swallow */
          }
          throw new Error(insJson.message || insJson.error || "insert_failed");
        }
        await reload();
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setUploading(false);
      }
    },
    [logement_id, reload],
  );

  async function handleSetCover(id) {
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        "/api/beds24-mapping?action=logement_gallery_set_cover",
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            action: "logement_gallery_set_cover",
            id,
            logement_id,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || "set_cover_failed");
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function handleDelete(id) {
    if (
      !window.confirm(
        "Supprimer cette photo définitivement ?\nCette action est irréversible.",
      )
    )
      return;
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        "/api/beds24-mapping?action=logement_gallery_delete",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "logement_gallery_delete",
            id,
            logement_id, // R-3 ownership obligatoire
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || "delete_failed");
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  const list = Array.isArray(gallery) ? gallery : [];

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {error && (
        <div
          style={{
            padding: 10,
            background: "#fce8e8",
            border: `1px solid ${DS.red}`,
            borderRadius: 7,
            color: DS.red,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          ❌ {error}
        </div>
      )}

      <label
        style={{
          display: "inline-block",
          padding: "8px 14px",
          background: uploading ? "#cbd5e1" : DS.skyDeep,
          color: "#fff",
          borderRadius: 8,
          cursor: uploading ? "not-allowed" : "pointer",
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 14,
        }}
      >
        {uploading ? "Envoi…" : "📤 Ajouter une photo"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleUpload}
          disabled={uploading}
          style={{ display: "none" }}
        />
      </label>

      {loading ? (
        <div
          style={{
            padding: 16,
            color: "#888",
            fontSize: 12,
            fontStyle: "italic",
          }}
        >
          Chargement de la galerie…
        </div>
      ) : list.length === 0 ? (
        <div
          style={{
            padding: 16,
            border: `1px dashed ${DS.border}`,
            borderRadius: 8,
            color: "#888",
            fontSize: 12,
            fontStyle: "italic",
            textAlign: "center",
          }}
        >
          Aucune photo dans la galerie pour l'instant.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
          }}
        >
          {list.map((photo) => (
            <div
              key={photo.id}
              style={{
                position: "relative",
                borderRadius: 8,
                overflow: "hidden",
                border: photo.is_cover
                  ? `2px solid ${DS.skyDeep}`
                  : `1px solid ${DS.border}`,
              }}
            >
              <img
                src={`${photo.public_url}?width=300&quality=80&format=webp`}
                alt=""
                loading="lazy"
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  display: "flex",
                  gap: 4,
                }}
              >
                <button
                  type="button"
                  onClick={() => handleSetCover(photo.id)}
                  title={photo.is_cover ? "Photo cover actuelle" : "Définir comme cover"}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    border: "none",
                    background: photo.is_cover ? "#fbbf24" : "rgba(255,255,255,0.85)",
                    color: photo.is_cover ? "#fff" : "#666",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  ★
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(photo.id)}
                  title="Supprimer"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    border: "none",
                    background: "rgba(255,255,255,0.85)",
                    color: DS.red,
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
